import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/lib/api';
import {
  GitMerge, Search, AlertTriangle, Clock, CheckCircle2, XCircle, RefreshCw,
  User, Calendar, Building2, ArrowRight, FileCheck, ShieldCheck, ThumbsUp, ThumbsDown,
} from 'lucide-react';

const STAGE_META = {
  manager:        { label: 'Manager',        color: 'bg-blue-500/10 text-blue-700 border-blue-500/30' },
  team_lead:      { label: 'Team Leader',    color: 'bg-cyan-500/10 text-cyan-700 border-cyan-500/30' },
  sales_manager:  { label: 'Sales Manager',  color: 'bg-violet-500/10 text-violet-700 border-violet-500/30' },
  hr:             { label: 'HR',             color: 'bg-amber-500/10 text-amber-700 border-amber-500/30' },
  ceo:            { label: 'CEO',            color: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30' },
  unknown:        { label: 'Unknown',        color: 'bg-muted text-muted-foreground border-border' },
};

const STATUS_PILL = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'approved') return 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30';
  if (s === 'rejected') return 'bg-red-500/10 text-red-700 border-red-500/30';
  if (s.startsWith('pending')) return 'bg-amber-500/10 text-amber-700 border-amber-500/30';
  return 'bg-muted text-foreground border-border';
};

function ChainBar({ chain = [], current, history = [] }) {
  const acted = new Set();
  history.forEach(h => h.action && acted.add(h.level));
  return (
    <div className="flex items-center flex-wrap gap-1">
      {chain.map((step, idx) => {
        const isCurrent = step === current;
        const isDone = acted.has(step);
        const meta = STAGE_META[step] || STAGE_META.unknown;
        return (
          <React.Fragment key={`${step}-${idx}`}>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border whitespace-nowrap
                ${isCurrent ? meta.color + ' ring-2 ring-amber-500/30 font-bold' : ''}
                ${isDone && !isCurrent ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30 line-through opacity-70' : ''}
                ${!isCurrent && !isDone ? 'bg-muted text-muted-foreground border-border opacity-50' : ''}`}>
              {meta.label}
            </span>
            {idx < chain.length - 1 && <ArrowRight className="h-2.5 w-2.5 text-muted-foreground" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function ApprovalPipelinePage() {
  const { user } = useAuth();
  const [data, setData] = useState({ regularizations: [], leaves: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState('all'); // all | leave | regularization
  const [filterStage, setFilterStage] = useState('all');
  const [search, setSearch] = useState('');
  const [days, setDays] = useState(60);
  const [actioningId, setActioningId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.set('type', filterType);
      params.set('days', days);
      const res = await api.get(`/hr/approval-pipeline?${params.toString()}`);
      setData(res.data || { regularizations: [], leaves: [], summary: {} });
    } catch {
      setData({ regularizations: [], leaves: [], summary: {} });
    } finally { setLoading(false); }
  }, [filterType, days]);

  // Inline approve/reject handlers — reused by PipelineRow.
  const performAction = useCallback(async (req, action) => {
    let comments = '';
    if (action === 'reject') {
      comments = window.prompt(`Reason for rejecting this ${req._kind} request?`);
      if (comments === null) return;       // user cancelled
      if (!comments.trim()) {
        toast.error('Rejection requires a reason');
        return;
      }
    }
    setActioningId(req.id);
    try {
      if (req._kind === 'leave') {
        await api.put(`/hr/leave-requests/${req.id}/approve`, { action, comments });
      } else if (req._kind === 'regularization') {
        const params = new URLSearchParams({ action });
        if (comments) params.set('comments', comments);
        await api.put(`/hr/regularization-requests/${req.id}/approve?${params.toString()}`);
      } else if (req._kind === 'timesheet') {
        // Timesheets: pending_manager → manager-action; manager_approved → hr-action
        const endpoint = req.status === 'pending_manager'
          ? `/sshr/timesheet/v2/${req.id}/manager-action`
          : `/sshr/timesheet/v2/${req.id}/hr-action`;
        await api.post(endpoint, { action, comment: comments });
      } else {
        throw new Error('Unsupported request type');
      }
      toast.success(`${action === 'approve' ? 'Approved' : 'Rejected'} successfully`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${action} failed`);
    } finally {
      setActioningId(null);
    }
  }, [load]);

  useEffect(() => { load(); }, [load]);

  const allItems = useMemo(() => {
    const regs = (data.regularizations || []).map(r => ({ ...r, _kind: 'regularization' }));
    const lvs  = (data.leaves || []).map(r => ({ ...r, _kind: 'leave' }));
    const tss  = (data.timesheets || []).map(r => ({ ...r, _kind: 'timesheet' }));
    let merged = [...regs, ...lvs, ...tss];
    if (filterType === 'pre_payroll') merged = [];  // pre_payroll shown in dedicated card
    if (filterStage !== 'all') {
      merged = merged.filter(r => {
        let lv;
        if (r._kind === 'timesheet') {
          lv = r.status === 'pending_manager' ? 'manager'
              : r.status === 'manager_approved' ? 'hr'
              : 'unknown';
        } else {
          lv = r.current_approver_level
            || (r.status || '').replace('pending_', '');
        }
        return lv === filterStage;
      });
    }
    if (search) {
      const s = search.toLowerCase();
      merged = merged.filter(r =>
        (r.employee_name || '').toLowerCase().includes(s)
        || (r.department || '').toLowerCase().includes(s)
        || (r.reason || '').toLowerCase().includes(s)
      );
    }
    merged.sort((a, b) => ((b.created_at || b.submitted_at) || '').localeCompare((a.created_at || a.submitted_at) || ''));
    return merged;
  }, [data, filterStage, search, filterType]);

  const pending = allItems.filter(r =>
    ['pending', 'pending_manager', 'pending_hr', 'pending_ceo', 'manager_approved'].includes(r.status));
  const completed = allItems.filter(r => ['approved', 'rejected', 'hr_approved', 'manager_rejected', 'hr_rejected'].includes(r.status));

  const reg = data.summary?.regularizations || {};
  const lv = data.summary?.leaves || {};
  const ts = data.summary?.timesheets || {};
  const pp = data.summary?.pre_payrolls || {};
  const stuck = data.summary?.stuck || [];

  const byStage = useMemo(() => {
    const merge = {};
    [reg.by_stage, lv.by_stage, ts.by_stage, pp.by_stage].forEach(b => {
      Object.entries(b || {}).forEach(([k, v]) => { merge[k] = (merge[k] || 0) + v; });
    });
    return merge;
  }, [reg.by_stage, lv.by_stage, ts.by_stage, pp.by_stage]);

  const allStages = Array.from(new Set([
    ...Object.keys(byStage),
    'manager', 'team_lead', 'sales_manager', 'hr', 'ceo',
  ]));

  return (
    <div className="space-y-6" data-testid="approval-pipeline-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <GitMerge className="h-6 w-6 text-violet-500" /> Approval Pipeline
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Every leave + regularization request, grouped by stage. See exactly who&apos;s holding things up.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="number" min={7} max={365}
            value={days} onChange={e => setDays(parseInt(e.target.value) || 60)}
            className="w-[100px]"
            data-testid="pipeline-days"
          />
          <span className="text-xs text-muted-foreground">days</span>
          <Button variant="ghost" size="icon" onClick={load} data-testid="pipeline-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">In-Flight Total</p>
          <p className="text-2xl font-bold text-amber-600">{data.summary?.total_in_flight || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Leaves</p>
          <p className="text-2xl font-bold">{lv.pending || 0}</p>
          <p className="text-[10px] text-muted-foreground">of {lv.total || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Regularizations</p>
          <p className="text-2xl font-bold">{reg.pending || 0}</p>
          <p className="text-[10px] text-muted-foreground">of {reg.total || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Timesheets</p>
          <p className="text-2xl font-bold">{ts.pending || 0}</p>
          <p className="text-[10px] text-muted-foreground">of {ts.total || 0}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Pre-Payroll</p>
          <p className="text-2xl font-bold">{pp.pending || 0}</p>
          <p className="text-[10px] text-muted-foreground">across {pp.total_months || 0} mo</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-xs text-muted-foreground">Stuck (&gt;3 days)</p>
          <p className="text-2xl font-bold text-red-600">{stuck.length}</p>
        </CardContent></Card>
      </div>

      {/* By-stage funnel */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Pending by Stage</CardTitle>
          <CardDescription>Click a stage to filter the list below</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterStage('all')}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${filterStage === 'all' ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}
              data-testid="pipeline-stage-all"
            >
              All Stages ({Object.values(byStage).reduce((a, b) => a + b, 0)})
            </button>
            {allStages.map(stage => {
              const meta = STAGE_META[stage] || STAGE_META.unknown;
              const count = byStage[stage] || 0;
              return (
                <button
                  key={stage}
                  onClick={() => setFilterStage(stage)}
                  className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${filterStage === stage ? meta.color + ' ring-2 ring-offset-1 ring-amber-500/30' : 'border-border hover:bg-muted'}`}
                  data-testid={`pipeline-stage-${stage}`}
                >
                  {meta.label} ({count})
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Pre-Payroll Verification Snapshot */}
      {(filterType === 'all' || filterType === 'pre_payroll') && (data.pre_payrolls || []).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-500" /> Pre-Payroll Verification
            </CardTitle>
            <CardDescription>HR sign-off status across the last 3 months</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {data.pre_payrolls.map(p => (
                <div key={p.month}
                  className={`p-3 rounded-lg border ${p.ready_to_run ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/5'}`}
                  data-testid={`pp-month-${p.month}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-semibold text-sm">{p.month}</p>
                    {p.ready_to_run ? (
                      <Badge className="bg-emerald-500/15 text-emerald-700 border-emerald-500/30">Ready</Badge>
                    ) : (
                      <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30">Pending</Badge>
                    )}
                  </div>
                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-2xl font-bold">{p.verified}<span className="text-sm text-muted-foreground">/{p.expected}</span></p>
                      <p className="text-[11px] text-muted-foreground">verified · {p.pending} pending</p>
                    </div>
                    <Button asChild size="sm" variant="outline" data-testid={`pp-go-${p.month}`}>
                      <a href={`/hr/payroll/pre-run`}>Open <ArrowRight className="h-3 w-3 ml-1" /></a>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stuck items alert */}
      {stuck.length > 0 && (
        <Card className="border-red-500/30 bg-red-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-4 w-4" /> Stuck Requests (pending &gt;3 days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {stuck.map(s => {
                const meta = STAGE_META[s.current_approver_level] || STAGE_META.unknown;
                return (
                  <div key={s.id} className="flex items-center justify-between text-sm border-b last:border-0 py-1.5">
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive" className="text-[10px]">{s.days_pending}d</Badge>
                      <span className="font-medium">{s.employee_name}</span>
                      <span className="text-xs text-muted-foreground">· {s.type} · {s.date}</span>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${meta.color}`}>
                      Waiting on {meta.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search employee / department / reason"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 w-[280px]"
            data-testid="pipeline-search"
          />
        </div>
        <div className="flex gap-1">
          {['all', 'leave', 'regularization', 'timesheet', 'pre_payroll'].map(t => (
            <Button
              key={t}
              size="sm"
              variant={filterType === t ? 'default' : 'outline'}
              onClick={() => setFilterType(t)}
              data-testid={`pipeline-type-${t}`}
            >{t === 'all' ? 'All'
              : t === 'pre_payroll' ? 'Pre-Payroll'
              : t.charAt(0).toUpperCase() + t.slice(1) + 's'}</Button>
          ))}
        </div>
        <Badge variant="outline">{allItems.length} matching</Badge>
      </div>

      {/* List */}
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="pipeline-tab-pending">
            Pending ({pending.length})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="pipeline-tab-completed">
            Completed ({completed.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-3 space-y-2">
          {loading ? (
            <div className="py-10 flex justify-center">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : pending.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
              <CheckCircle2 className="h-10 w-10 mx-auto opacity-30 mb-2" />
              No pending requests in this filter.
            </CardContent></Card>
          ) : pending.map(r => <PipelineRow key={`${r._kind}-${r.id}`} req={r} userRole={user?.role} onAction={performAction} actioning={actioningId === r.id} />)}
        </TabsContent>

        <TabsContent value="completed" className="mt-3 space-y-2">
          {completed.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">
              No completed requests in this range.
            </CardContent></Card>
          ) : completed.map(r => <PipelineRow key={`${r._kind}-${r.id}`} req={r} userRole={user?.role} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Map: which roles can action at which stage. Mirrors backend role_allowed.
const ROLE_CAN_ACTION = {
  super_admin: new Set(['team_lead', 'manager', 'sales_manager', 'hr', 'ceo']),
  admin:       new Set(['team_lead', 'manager', 'sales_manager', 'hr', 'ceo']),
  team_leader: new Set(['team_lead', 'manager']),
  sales_manager: new Set(['sales_manager']),
  hr:          new Set(['hr']),
  ceo:         new Set(['ceo']),
  cs_head:     new Set(['manager']),
  marketing_admin: new Set(['manager']),
  master_of_academics: new Set(['manager']),
  finance:     new Set(['hr']),
  coo:         new Set(['hr', 'manager']),
};

function PipelineRow({ req, userRole, onAction, actioning }) {
  const isLeave = req._kind === 'leave';
  const isTimesheet = req._kind === 'timesheet';
  // Timesheet stage derivation
  let stageLevel;
  if (isTimesheet) {
    stageLevel = req.status === 'pending_manager' ? 'manager'
      : req.status === 'manager_approved' ? 'hr'
      : 'unknown';
  } else {
    stageLevel = req.current_approver_level
      || (req.status || '').replace('pending_', '');
  }
  const stageMeta = STAGE_META[stageLevel] || STAGE_META.unknown;
  const tsField = req.created_at || req.submitted_at;
  const created = tsField ? new Date(tsField) : null;
  const ageDays = created ? Math.floor((Date.now() - created.getTime()) / 86400000) : null;
  const lastAction = (req.approval_history || []).slice(-1)[0];

  // Timesheet pseudo-chain
  const chain = isTimesheet ? ['manager', 'hr'] : (req.approval_chain || []);
  const history = isTimesheet
    ? [
        ...(req.manager_actioned_at ? [{ level: 'manager', action: req.status.includes('rejected') ? 'reject' : 'approve' }] : []),
        ...(req.hr_actioned_at ? [{ level: 'hr', action: req.status.includes('rejected') ? 'reject' : 'approve' }] : []),
      ]
    : (req.approval_history || []);

  const Icon = isLeave ? Calendar : isTimesheet ? FileCheck : Clock;
  const iconBg = isLeave ? 'bg-blue-500/10 text-blue-600'
    : isTimesheet ? 'bg-violet-500/10 text-violet-600'
    : 'bg-amber-500/10 text-amber-600';

  return (
    <Card className="hover:bg-muted/20" data-testid={`pipeline-row-${req.id}`}>
      <CardContent className="p-3 space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}>
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium text-sm">{req.employee_name}</p>
                <Badge variant="outline" className="text-[10px]">
                  {isLeave ? (req.leave_type || 'leave').replace('_', ' ')
                    : isTimesheet ? 'timesheet'
                    : 'regularization'}
                </Badge>
                {req.department && (
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Building2 className="h-3 w-3" /> {req.department}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isLeave
                  ? `${req.start_date} → ${req.end_date} · ${req.leave_days || 0} day(s)`
                  : isTimesheet
                  ? `${req.date} · ${req.total_hours || 0}h across ${(req.rows || []).length} task(s)${req.is_late ? ' · LATE' : ''}`
                  : `${req.date} · ${req.original_in || '—'} → ${req.corrected_in || '—'}`}
              </p>
              {req.reason && (
                <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[640px]">
                  {req.reason}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_PILL(req.status)}`}>
              {(req.status || '').replace(/_/g, ' ')}
            </span>
            {ageDays !== null && (
              <span className={`text-[10px] ${ageDays > 3 ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                {ageDays}d ago
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pl-12">
          <ChainBar chain={chain} current={stageLevel} history={history} />
          <div className="flex items-center gap-2">
            {(req.status === 'pending'
              || (req.status || '').startsWith('pending_')
              || req.status === 'manager_approved') ? (
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${stageMeta.color}`}>
                <User className="inline h-3 w-3 mr-1" />
                Waiting on {stageMeta.label}
              </span>
            ) : null}
            {/* Inline approve/reject buttons — only when this user can action this stage */}
            {onAction && userRole && (ROLE_CAN_ACTION[userRole] || new Set()).has(stageLevel) && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-emerald-700 border-emerald-500/40 hover:bg-emerald-500/10"
                  disabled={actioning}
                  onClick={() => onAction(req, 'approve')}
                  data-testid={`pipeline-approve-${req.id}`}
                >
                  <ThumbsUp className="h-3 w-3 mr-1" /> Approve
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-red-700 border-red-500/40 hover:bg-red-500/10"
                  disabled={actioning}
                  onClick={() => onAction(req, 'reject')}
                  data-testid={`pipeline-reject-${req.id}`}
                >
                  <ThumbsDown className="h-3 w-3 mr-1" /> Reject
                </Button>
              </>
            )}
          </div>
        </div>

        {lastAction && (
          <p className="text-[11px] text-muted-foreground pl-12">
            Last action: <span className="font-medium">{lastAction.action || lastAction.status}</span>
            {lastAction.approved_by_name && ` by ${lastAction.approved_by_name}`}
            {lastAction.comments && ` — "${lastAction.comments}"`}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
